import {
    GROUP_ADMINS,
    GROUP_PARTNER_ADMINS,
    GROUP_PARTNER_MANAGERS,
    GROUP_PARTNER_SUBSCRIPTION_EDITOR,
    GROUP_SERVICE_ADMINS,
    GROUP_VIEWER,
} from './acm.groups';

export type CrudPermissions = {
    create: string[];
    read: string[];
    update: string[];
    delete: string[];
};

/**
 * Defines ALLOW permissions in the hierarchy of entity.action.requiredGroups
 *
 * Although CRUD action verbs are used, it is not the intent to limit actions to CRUD actions.
 * entities and actions can be defined as specifically as desired.
 *
 * The point of this permissions structure is that it abstracts away the specific group names
 * so that a template can guard a feature like so:
 * ```
 * <button *hasAnyGroup="permissions.partner.delete">
 *  Delete
 * </button>
 * ```
 * This allows us to define the access to UI features in terms of what kind of actions the user should be allowed
 * to do without having to couple those concepts to a particular group/role name or set of names.
 *
 * This also centralizes the authorization policy so that we can modify existing feature toggles in one place
 * rather than having to update every component template when we decide that a "SuperAdmin" should also be allowed
 * to use a given feature.
 *
 **/
export const acmGroupPermissions = {
    // NOTE: don't give a type to this object because doing so will only hide the specificity of the implicit type
    // and make it more difficult to add custom action permissions later.
    /**
     * Customers (aka Partners)
     */
    partner: {
        create: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
        read: GROUP_VIEWER,
        update: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
        delete: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
    },
    partnerContact: {
        create: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
        read: GROUP_VIEWER,
        update: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
        delete: [...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
    },
    partnerServiceSubscription: {
        create: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
        read: GROUP_VIEWER,
        update: [
            ...GROUP_PARTNER_SUBSCRIPTION_EDITOR,
            ...GROUP_PARTNER_MANAGERS,
            ...GROUP_PARTNER_ADMINS,
            ...GROUP_ADMINS,
        ],
        delete: [...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],
    },
    partnerCredentials: {
        // not used
        create: [],
        // can view partner credentials tab
        read: GROUP_VIEWER,
        // (can provision, replace, deactivate)
        update: [...GROUP_PARTNER_MANAGERS, ...GROUP_PARTNER_ADMINS, ...GROUP_ADMINS],

        delete: [],
    },
    /**
     * Services
     */
    partnerServices: {
        create: [...GROUP_SERVICE_ADMINS, ...GROUP_ADMINS],
        read: [...GROUP_SERVICE_ADMINS, ...GROUP_ADMINS],
        update: [...GROUP_SERVICE_ADMINS, ...GROUP_ADMINS],
        delete: [...GROUP_SERVICE_ADMINS, ...GROUP_ADMINS],
    },
};

export type CrudEntityType = keyof typeof acmGroupPermissions;
